
import { FC, useState, useEffect } from 'react';
import { useSupabase, uploadImage, deleteImage } from '../../../hooks/useSupabase';

interface HeroSlide {
  id: number;
  image: string;
  title: string;
  subtitle: string;
}

const HeroManagement: FC = () => {
  const { supabase } = useSupabase();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newSlideImage, setNewSlideImage] = useState('');

  // 초기 데이터 로드
  useEffect(() => {
    loadSlides();
  }, [supabase]);

  const loadSlides = async () => {
    if (!supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('hero_slides')
        .select('*')
        .order('id');

      if (error && error.code !== 'PGRST116') {
        console.error('슬라이드 로드 실패:', error);
        // 기본 데이터로 초기화
        await initializeDefaultSlides();
        return;
      }

      if (data && data.length > 0) {
        setSlides(data);
      } else {
        // 데이터가 없으면 기본 데이터 생성
        await initializeDefaultSlides();
      }
    } catch (error) {
      console.error('슬라이드 로드 중 오류:', error);
      await initializeDefaultSlides();
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultSlides = async () => {
    const defaultSlides = [
      {
        image: 'https://public.readdy.ai/ai/img_res/0d698196072199b3ede55baaa85b84de.jpg',
        title: '30년 경력 셰프의 정성',
        subtitle: '최상급 재료로 만드는 프리미엄 한식'
      },
      {
        image: 'https://readdy.ai/api/search-image?query=Premium%20Iberico%20pork%20being%20grilled%20on%20traditional%20Korean%20BBQ%20grill%2C%20high%20quality%20black%20pork%20with%20marbling%2C%20professional%20kitchen%20setting%2C%20warm%20lighting%2C%20traditional%20Korean%20restaurant%20atmosphere&width=1920&height=1080&seq=hero2&orientation=landscape',
        title: '최상급 이베리코 흑돼지',
        subtitle: '스페인 직수입 프리미엄 돼지고기'
      },
      {
        image: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20stone%20pot%20rice%20cooking%20with%20steam%20rising%2C%20small%20black%20stone%20pot%20with%20freshly%20cooked%20white%20rice%2C%20traditional%20Korean%20restaurant%20kitchen%2C%20warm%20cozy%20lighting%2C%20authentic%20Korean%20dining%20experience&width=1920&height=1080&seq=hero3&orientation=landscape',
        title: '즉석 가마솥밥',
        subtitle: '주문 즉시 지어드리는 따뜻한 밥'
      },
      {
        image: 'https://public.readdy.ai/ai/img_res/8109e5d8-b9c4-42b5-b757-96fc15371eec.jpg',
        title: '넓고 편안한 95석 공간',
        subtitle: '혼밥부터 가족 외식까지'
      }
    ];

    setSlides(defaultSlides);

    // 데이터베이스에 저장 (ID 제외)
    if (supabase) {
      try {
        // 기존 데이터가 있는지 확인
        const { data: existingData } = await supabase
          .from('hero_slides')
          .select('id')
          .limit(1);

        // 기존 데이터가 없을 때만 기본 데이터 삽입
        if (!existingData || existingData.length === 0) {
          const { error } = await supabase
            .from('hero_slides')
            .insert(defaultSlides.map(slide => ({
              image: slide.image,
              title: slide.title,
              subtitle: slide.subtitle
            })));

          if (error) {
            console.error('기본 슬라이드 저장 실패:', error);
          }
        }
      } catch (error) {
        console.error('기본 슬라이드 저장 중 오류:', error);
      }
    }
  };

  const handleEdit = (slide: HeroSlide) => {
    setEditingSlide({ ...slide });
  };

  const handleSave = async () => {
    if (!editingSlide || !supabase) return;

    try {
      const { error } = await supabase
        .from('hero_slides')
        .update({
          image: editingSlide.image,
          title: editingSlide.title,
          subtitle: editingSlide.subtitle
        })
        .eq('id', editingSlide.id);

      if (error) throw error;

      setSlides(slides.map(slide => 
        slide.id === editingSlide.id ? editingSlide : slide
      ));
      setEditingSlide(null);
      alert('슬라이드가 성공적으로 수정되었습니다.');
    } catch (error) {
      console.error('슬라이드 수정 실패:', error);
      alert('슬라이드 수정에 실패했습니다.');
    }
  };

  const handleDelete = async (id: number) => {
    if (slides.length <= 1) {
      alert('최소 1개의 슬라이드는 유지되어야 합니다.');
      return;
    }

    if (!confirm('정말로 이 슬라이드를 삭제하시겠습니까?')) return;

    if (!supabase) return;

    try {
      const { error } = await supabase
        .from('hero_slides')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSlides(slides.filter(slide => slide.id !== id));
      alert('슬라이드가 성공적으로 삭제되었습니다.');
    } catch (error) {
      console.error('슬라이드 삭제 실패:', error);
      alert('슬라이드 삭제에 실패했습니다.');
    }
  };

  const handleAdd = async (newSlide: Omit<HeroSlide, 'id'>) => {
    if (!supabase) return;

    try {
      // ID를 제외하고 데이터 삽입 (데이터베이스에서 자동 생성)
      const { data, error } = await supabase
        .from('hero_slides')
        .insert([{
          image: newSlide.image,
          title: newSlide.title,
          subtitle: newSlide.subtitle
        }])
        .select()
        .single();

      if (error) throw error;

      setSlides([...slides, data]);
      setShowAddForm(false);
      setNewSlideImage('');
      alert('새 슬라이드가 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('슬라이드 추가 실패:', error);
      alert('슬라이드 추가에 실패했습니다.');
    }
  };

  const generateImageUrl = (prompt: string) => {
    const seq = `hero-${Date.now()}`;
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=1920&height=1080&seq=${seq}&orientation=landscape`;
  };

  const handleImageGenerate = (prompt: string, isEdit: boolean = false) => {
    if (!prompt.trim()) {
      alert('이미지 설명을 입력해주세요.');
      return;
    }

    const imageUrl = generateImageUrl(prompt);
    
    if (isEdit && editingSlide) {
      setEditingSlide({ ...editingSlide, image: imageUrl });
    } else {
      setNewSlideImage(imageUrl);
    }
  };

  const handleImageUpload = async (file: File, isEdit: boolean = false) => {
    if (!file) return;

    // 파일 크기 체크 (5MB 제한)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    // 파일 형식 체크
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    setUploading(true);
    try {
      // 파일명을 안전한 형태로 생성 (영문, 숫자만 사용)
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `hero-${timestamp}.${fileExtension}`;
      
      const imageUrl = await uploadImage(file, fileName);
      
      if (isEdit && editingSlide) {
        setEditingSlide({ ...editingSlide, image: imageUrl });
      } else {
        setNewSlideImage(imageUrl);
      }
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (imageUrl: string, isEdit: boolean = false) => {
    if (!confirm('이미지를 삭제하시겠습니까?')) return;

    try {
      // Supabase Storage에서 이미지 삭제
      if (imageUrl.includes('supabase')) {
        const path = imageUrl.split('/').pop();
        if (path) {
          await deleteImage(path);
        }
      }

      if (isEdit && editingSlide) {
        setEditingSlide({ ...editingSlide, image: '' });
      } else {
        setNewSlideImage('');
      }
    } catch (error) {
      console.error('이미지 삭제 실패:', error);
      alert('이미지 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin mb-4"></i>
          <p className="text-gray-600">슬라이드 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">홈화면 슬라이드 관리</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          슬라이드 추가
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {slides.map((slide) => (
          <div key={slide.id} className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-48 object-cover object-center"
            />
            <div className="p-4">
              <h3 className="font-bold text-lg mb-2">{slide.title}</h3>
              <p className="text-gray-600 mb-4">{slide.subtitle}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(slide)}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-1"></i>
                  수정
                </button>
                <button
                  onClick={() => handleDelete(slide.id)}
                  disabled={slides.length <= 1}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  삭제
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editingSlide && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">슬라이드 수정</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">이미지</label>
                {editingSlide.image && (
                  <div className="relative">
                    <img
                      src={editingSlide.image}
                      alt="미리보기"
                      className="w-full h-32 object-cover rounded-lg mb-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleImageDelete(editingSlide.image, true)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                )}
                
                <div className="space-y-3">
                  {/* 파일 업로드 */}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleImageUpload(file, true);
                        }
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG, GIF 파일 (최대 5MB)
                    </p>
                  </div>
                  
                  <div className="text-center text-gray-400">또는</div>
                  
                  {/* AI 이미지 생성 */}
                  <div>
                    <input
                      type="text"
                      placeholder="이미지 설명을 입력하세요 (예: 한식당 내부 모습, 고급스러운 분위기)"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleImageGenerate(e.currentTarget.value, true);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      이미지 설명을 입력하고 Enter를 누르면 AI가 이미지를 생성합니다
                    </p>
                  </div>
                </div>
                
                {uploading && (
                  <div className="flex items-center justify-center py-4">
                    <i className="ri-loader-4-line text-2xl text-blue-600 animate-spin mr-2"></i>
                    <span className="text-sm text-gray-600">이미지 업로드 중...</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">제목</label>
                <input
                  type="text"
                  value={editingSlide.title}
                  onChange={(e) => setEditingSlide({...editingSlide, title: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">부제목</label>
                <input
                  type="text"
                  value={editingSlide.subtitle}
                  onChange={(e) => setEditingSlide({...editingSlide, subtitle: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSave}
                disabled={uploading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                저장
              </button>
              <button
                onClick={() => setEditingSlide(null)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">새 슬라이드 추가</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const title = formData.get('title') as string;
              const subtitle = formData.get('subtitle') as string;
              
              if (!newSlideImage) {
                alert('이미지를 업로드하거나 생성해주세요.');
                return;
              }
              
              handleAdd({
                image: newSlideImage,
                title,
                subtitle
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">이미지</label>
                  {newSlideImage && (
                    <div className="relative">
                      <img
                        src={newSlideImage}
                        alt="미리보기"
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                      <button
                        type="button"
                        onClick={() => handleImageDelete(newSlideImage, false)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    {/* 파일 업로드 */}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, false);
                          }
                        }}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        disabled={uploading}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG, GIF 파일 (최대 5MB)
                      </p>
                    </div>
                    
                    <div className="text-center text-gray-400">또는</div>
                    
                    {/* AI 이미지 생성 */}
                    <div>
                      <input
                        type="text"
                        placeholder="이미지 설명을 입력하세요 (예: 한식당 내부 모습, 고급스러운 분위기)"
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleImageGenerate(e.currentTarget.value, false);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        이미지 설명을 입력하고 Enter를 누르면 AI가 이미지를 생성합니다
                      </p>
                    </div>
                  </div>
                  
                  {uploading && (
                    <div className="flex items-center justify-center py-4">
                      <i className="ri-loader-4-line text-2xl text-blue-600 animate-spin mr-2"></i>
                      <span className="text-sm text-gray-600">이미지 업로드 중...</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">제목</label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">부제목</label>
                  <input
                    type="text"
                    name="subtitle"
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  disabled={uploading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  추가
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewSlideImage('');
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroManagement;
